/**
 * Top-level Prospects admin page.
 * Owns tab/view state for Kanban / List / Detail / Settings (sub-tabbed).
 * Mirrors the Affiliates.tsx pattern (internal state-based sub-routing).
 */

import { useState } from 'react';
import { Kanban, List, Settings } from 'lucide-react';
import ProspectsKanban from './prospects/ProspectsKanban';
import ProspectsList from './prospects/ProspectsList';
import ProspectDetail from './prospects/ProspectDetail';
import ProspectsSettings from './prospects/ProspectsSettings';

type View =
  | { kind: 'kanban' }
  | { kind: 'list' }
  | { kind: 'detail'; prospectId: string }
  | { kind: 'settings' };

const tabs: Array<{
  id: 'kanban' | 'list' | 'settings';
  label: string;
  icon: typeof Kanban;
}> = [
  { id: 'kanban', label: 'Pipeline', icon: Kanban },
  { id: 'list', label: 'List', icon: List },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function Prospects() {
  const [view, setView] = useState<View>({ kind: 'kanban' });

  // Tab to highlight in the nav. Detail belongs to whichever list-view brought
  // the admin there; we anchor it to 'kanban' so the admin can see they're
  // inside the Pipeline subtree.
  const activeTab =
    view.kind === 'detail' ? 'kanban' : view.kind === 'settings' ? 'settings' : view.kind;

  const renderView = () => {
    switch (view.kind) {
      case 'kanban':
        return (
          <ProspectsKanban
            onOpenProspect={(id) => setView({ kind: 'detail', prospectId: id })}
          />
        );
      case 'list':
        return (
          <ProspectsList
            onOpenProspect={(id) => setView({ kind: 'detail', prospectId: id })}
          />
        );
      case 'detail':
        return (
          <ProspectDetail
            prospectId={view.prospectId}
            onBack={() => setView({ kind: 'kanban' })}
          />
        );
      case 'settings':
        return <ProspectsSettings />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Tab nav */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setView({ kind: t.id })}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Active view */}
      <div>{renderView()}</div>
    </div>
  );
}
